<template>
  <div>
    <CCard>
      <CCardHeader>
        <strong>BGP 外部 AS 端点</strong>
        <small class="text-muted ml-2">对端非系统 Router 时的 AS 端点（地址 + AS + 备注），创建后不可修改，在用不可删除</small>
        <div class="float-right">
          <CButtonGroup>
            <CButton color="primary" size="sm" @click="openModal()">新增端点</CButton>
            <CButton color="secondary" size="sm" :disabled="loading" @click="loadList">刷新</CButton>
          </CButtonGroup>
        </div>
      </CCardHeader>
      <CCardBody>
        <CDataTable
          :items="endpoints"
          :fields="fields"
          hover
          striped
          :loading="loading"
          pagination
          :items-per-page="15"
        >
          <template #owner_type="{ item }">
            <td class="align-middle">
              <CBadge :color="item.owner_type === 'cmdb' ? 'info' : 'secondary'">
                {{ item.owner_type === 'cmdb' ? 'CMDB 设备' : '外部' }}
              </CBadge>
            </td>
          </template>
          <template #address="{ item }">
            <td class="align-middle"><code>{{ item.address }}</code></td>
          </template>
          <template #as_num="{ item }">
            <td class="align-middle">AS {{ item.as_num }}</td>
          </template>
          <template #used_count="{ item }">
            <td class="align-middle">
              <CBadge :color="item.used_count > 0 ? 'success' : 'light'">
                {{ item.used_count > 0 ? item.used_count + ' 个 connection' : '未使用' }}
              </CBadge>
            </td>
          </template>
          <template #action="{ item }">
            <td class="align-middle">
              <CButton color="danger" size="sm" :disabled="item.used_count > 0" @click="deleteItem(item)">
                删除
              </CButton>
            </td>
          </template>
        </CDataTable>
      </CCardBody>
    </CCard>

    <!-- 新增 Modal -->
    <CModal :show.sync="modal.show" title="新增 BGP 端点" color="primary">
      <CForm>
        <CSelect label="类型" v-model="modal.form.owner_type" :options="[
          { value: 'external', label: '外部（非系统管理设备）' },
          { value: 'cmdb', label: 'CMDB 设备' }
        ]" />
        <CSelect
          v-if="modal.form.owner_type === 'cmdb'"
          label="CMDB 设备"
          v-model="modal.form.cmdb_id"
          :options="cmdbOptions"
          placeholder="请选择设备"
          @change="onCmdbChange"
        />
        <CInput label="名称" v-model="modal.form.name" placeholder="如 transit-he-other-as12345" />
        <CInput label="地址（IP 或域名）" v-model="modal.form.address" placeholder="如 22.9.17.1 或 peer.example.com" />
        <CInput label="AS 号" v-model="modal.form.as_num" placeholder="如 29632" />
        <CTextarea label="备注" v-model="modal.form.remark" :rows="2" />
      </CForm>
      <template #footer>
        <CButtonGroup>
          <CButton color="danger" @click="modal.show = false">取消</CButton>
          <CButton color="success" :disabled="modal.loading" @click="submitItem">
            {{ modal.loading ? '创建中...' : '创建' }}
          </CButton>
        </CButtonGroup>
      </template>
    </CModal>

    <!-- 通用确认 Modal -->
    <CModal :show.sync="confirmModal.show" :title="confirmModal.title" color="primary">
      <p>{{ confirmModal.message }}</p>
      <template #footer>
        <CButtonGroup>
          <CButton color="danger" @click="confirmModal.show = false">取消</CButton>
          <CButton color="success" @click="confirmModal.callback(); confirmModal.show = false">确认</CButton>
        </CButtonGroup>
      </template>
    </CModal>
  </div>
</template>

<script>
export default {
  name: 'BgpEndpointList',
  data() {
    return {
      loading: false,
      endpoints: [],
      cmdbDevices: [],
      fields: [
        { key: 'name', label: '名称', _style: 'min-width:180px' },
        { key: 'owner_type', label: '类型', _style: 'width:110px' },
        { key: 'cmdb_name', label: 'CMDB 设备', _style: 'width:140px' },
        { key: 'address', label: '地址', _style: 'width:180px' },
        { key: 'as_num', label: 'AS', _style: 'width:100px' },
        { key: 'remark', label: '备注', _style: 'min-width:120px' },
        { key: 'used_count', label: '使用状态', _style: 'width:140px' },
        { key: 'action', label: '操作', _style: 'width:80px' }
      ],
      modal: {
        show: false,
        loading: false,
        form: { owner_type: 'external', cmdb_id: '', name: '', address: '', as_num: '', remark: '' }
      },
      confirmModal: {
        show: false, title: '确认', message: '', callback: () => {}
      }
    }
  },
  computed: {
    cmdbOptions() {
      return this.cmdbDevices.map(d => ({ value: d.id, label: `${d.name} (${d.main_ip || '无IP'})` }))
    }
  },
  mounted() {
    this.loadList()
    this.loadCmdb()
  },
  methods: {
    async loadCmdb() {
      try {
        const res = await this.$axios.get('user/cmdb/device')
        if (res.data.code === 200) {
          this.cmdbDevices = res.data.devices || []
        }
      } catch (e) { /* ignore */ }
    },
    async loadList() {
      this.loading = true
      try {
        const res = await this.$axios.get('user/bgp/endpoint/list')
        if (res.data.code === 200) {
          this.endpoints = res.data.data || []
        } else {
          this.$toasted.show(res.data.msg || '加载失败', { type: 'error' })
        }
      } catch (e) {
        this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
      }
      this.loading = false
    },
    onCmdbChange() {
      const d = this.cmdbDevices.find(x => x.id === this.modal.form.cmdb_id)
      if (d) {
        if (!this.modal.form.name) this.modal.form.name = d.name
        if (!this.modal.form.address && d.main_ip) this.modal.form.address = d.main_ip
      }
    },
    openModal() {
      this.modal.form = { owner_type: 'external', cmdb_id: '', name: '', address: '', as_num: '', remark: '' }
      this.modal.show = true
    },
    async submitItem() {
      const f = this.modal.form
      if (f.owner_type === 'cmdb' && !f.cmdb_id) {
        this.$toasted.show('请选择 CMDB 设备', { type: 'error' })
        return
      }
      if (!f.name || !f.address || !f.as_num) {
        this.$toasted.show('名称、地址、AS 号必填', { type: 'error' })
        return
      }
      this.modal.loading = true
      try {
        const res = await this.$axios.post('user/bgp/endpoint/add', f)
        if (res.data.code === 200) {
          this.$toasted.show('已创建', { type: 'success' })
          this.modal.show = false
          this.loadList()
        } else {
          this.$toasted.show(res.data.msg || '创建失败', { type: 'error' })
        }
      } catch (e) {
        this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
      }
      this.modal.loading = false
    },
    deleteItem(item) {
      this.confirmModal.title = '确认删除'
      this.confirmModal.message = `确认删除端点 "${item.name}"（${item.address} AS${item.as_num}）？`
      this.confirmModal.callback = async () => {
        try {
          const res = await this.$axios.delete(`user/bgp/endpoint/${item.id}/delete`)
          if (res.data.code === 200) {
            this.$toasted.show('已删除', { type: 'success' })
            this.loadList()
          } else {
            this.$toasted.show(res.data.msg || '删除失败', { type: 'error' })
          }
        } catch (e) {
          this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
        }
      }
      this.confirmModal.show = true
    }
  }
}
</script>
